/* Senzo Event-Konfigurator, wird nur von sections/custom-event-configurator.liquid geladen. */
(() => {
  if (window.customElements.get('event-configurator')) return;

  const LEVELS = ['recommended', 'reserve'];
  const LINES = ['santino', 'senzo', 'label'];
  const SORTS = ['sparkling', 'white', 'rose', 'red'];
  const BOTTLE_ML = 750;
  const CARTON = 6; // Alle Mengen in vollen 6er-Kartons
  const GUESTS_RANGE = { min: 5, max: 250 };

  // Aufteilung des Stillweins je Geschmack. Fehlt eine Sorte im Sortiment, wird automatisch neu verteilt.
  const TASTE_SHARES = {
    mixed: { white: 0.45, rose: 0.2, red: 0.35 },
    white: { white: 0.8, red: 0.2 },
    red: { red: 0.8, white: 0.2 },
    rose: { rose: 0.7, white: 0.3 },
    sparkling: { white: 0.6, red: 0.4 }
  };
  // Bei „Bunt gemischt“ kommt Rosé nur dazu, wenn die gewählte Linie selbst einen hat.
  const LINE_ONLY = { mixed: ['rose'] };
  // Bei „Prickelnd“ wird die Hälfte des Stillweins durch Prickelndes ersetzt.
  const SPARKLING_TASTE_SHIFT = 0.5;
  // Für den Hinweis, wenn eine Sorte aus einer anderen Linie kommt.
  const MISSING_SORT = { sparkling: 'nichts Prickelndes', white: 'keinen Weißwein', red: 'keinen Rotwein', rose: 'keinen Rosé' };

  const ceil = (x) => Math.ceil(x - 1e-9);
  const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
  const toInt = (v) => parseInt(v, 10) || 0;
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  /* ---------------------------------------------------------------- Rechnung */

  // Bedarf in ml aus den Antworten (unabhängig vom Sortiment).
  function calculate(a, f) {
    const occasionFactor = (f.occasion[a.occasion] ?? 100) / 100;
    const nonAlc = a.nonalc;
    const switchers = a.mixed ? (a.guests - nonAlc) * f.switcherShare / 100 : 0;
    const people = nonAlc + switchers;

    const hasReception = a.reception;
    const receptionHours = hasReception ? Math.min(1, a.hours) : 0;
    const sparklingGlasses = hasReception ? f.receptionGlasses : 0;

    const plannedMealHours = a.food === 'menu' ? f.menuHours : a.food === 'buffet' ? f.buffetHours : 0;
    const plannedMealGlasses = a.food === 'menu' ? f.menuGlasses : a.food === 'buffet' ? f.buffetGlasses : 0;
    const mealHours = Math.min(plannedMealHours, Math.max(0, a.hours - receptionHours));
    const mealGlasses = plannedMealHours ? plannedMealGlasses * mealHours / plannedMealHours : 0;

    const freeHours = Math.max(0, a.hours - receptionHours - mealHours);
    const earlyHours = Math.min(freeHours, f.earlyHours);
    const freeGlasses = earlyHours * f.rateEarly + (freeHours - earlyHours) * f.rateLate;

    let stillMl = (mealGlasses + freeGlasses) * f.glassMlWine;
    let sparklingMl = sparklingGlasses * f.glassMlSparkling;
    if (a.taste === 'sparkling') {
      sparklingMl += stillMl * SPARKLING_TASTE_SHIFT;
      stillMl *= 1 - SPARKLING_TASTE_SHIFT;
    }

    const scale = people * occasionFactor;
    return {
      nonAlc, switchers, people, occasionFactor,
      sparklingGlasses, mealGlasses, freeHours, freeGlasses,
      stillMl: stillMl * scale,
      sparklingMl: sparklingMl * scale
    };
  }

  // n ganze Einheiten nach Anteilen verteilen (Largest-Remainder, Summe bleibt exakt n).
  function distribute(n, shares) {
    const parts = Object.keys(shares).map((key) => ({ key, value: n * shares[key] }));
    const result = {};
    let used = 0;
    parts.forEach(({ key, value }) => { result[key] = Math.floor(value); used += result[key]; });
    parts.sort((x, y) => (y.value - Math.floor(y.value)) - (x.value - Math.floor(x.value)));
    for (let i = 0; used < n && parts.length; i++, used++) result[parts[i % parts.length].key]++;
    return result;
  }

  // Kartons je Wein für eine Stufe. Erst die Gesamtzahl Kartons bestimmen, dann ganze Kartons auf die Sorten verteilen.
  function buildPlan(calc, taste, level, f, resolve) {
    let stillMl = calc.stillMl;
    let sparklingMl = calc.sparklingMl;

    const lineOnly = LINE_ONLY[taste] || [];
    const shares = { ...(TASTE_SHARES[taste] || TASTE_SHARES.mixed) };
    Object.keys(shares).forEach((sort) => { if (!resolve(sort, lineOnly.includes(sort))) delete shares[sort]; });
    const shareSum = Object.values(shares).reduce((sum, v) => sum + v, 0);

    if (!resolve('sparkling')) { stillMl += sparklingMl; sparklingMl = 0; }
    if (!shareSum) { sparklingMl += stillMl; stillMl = 0; }

    const need = {};
    if (sparklingMl > 0) need.sparkling = sparklingMl / BOTTLE_ML;
    Object.keys(shares).forEach((sort) => { need[sort] = stillMl * shares[sort] / shareSum / BOTTLE_ML; });
    const bottlesNeeded = Object.values(need).reduce((sum, v) => sum + v, 0);
    if (bottlesNeeded <= 0) return [];

    const recommended = Math.max(1, ceil(bottlesNeeded * (1 + f.bufferRecommended / 100) / CARTON));
    const cartons = level === 'recommended'
      ? recommended
      : Math.max(recommended + 1, ceil(bottlesNeeded * (1 + f.bufferReserve / 100) / CARTON));

    const quota = {};
    Object.keys(need).forEach((sort) => { quota[sort] = need[sort] / bottlesNeeded; });
    const allocation = distribute(cartons, quota);

    // Wer anstößt, bekommt ab zwei Kartons immer mindestens einen Karton Prickelndes.
    if (need.sparkling && !allocation.sparkling && cartons >= 2) {
      const donor = Object.keys(allocation).filter((sort) => sort !== 'sparkling')
        .reduce((max, sort) => (allocation[sort] > (allocation[max] || 0) ? sort : max), null);
      if (donor) { allocation[donor]--; allocation.sparkling = 1; }
    }

    const items = [];
    SORTS.forEach((sort) => {
      const count = allocation[sort] || 0;
      const slot = count && resolve(sort, lineOnly.includes(sort));
      if (!slot) return;
      const existing = items.find((item) => item.slot.key === slot.key);
      if (existing) existing.cartons += count;
      else items.push({ sort, slot, cartons: count });
    });
    return items;
  }

  // Preis eines Weins: 6er-Bundle, wenn vorhanden, sonst 6 Einzelflaschen.
  function packaging(slot, cartons) {
    const bundle = slot.bundle && slot.bundle.available ? slot.bundle : null;
    const cartonPrice = bundle ? bundle.price : CARTON * slot.single.price;
    return {
      bundle,
      cartonPrice,
      bottles: cartons * CARTON,
      price: cartons * cartonPrice,
      saving: bundle ? Math.max(0, cartons * (CARTON * slot.single.price - bundle.price)) : 0
    };
  }

  const planBottles = (items) => items.reduce((sum, item) => sum + item.cartons * CARTON, 0);
  const planPrice = (items) => items.reduce((sum, item) => sum + packaging(item.slot, item.cartons).price, 0);

  /* ---------------------------------------------------------------- Element */

  class EventConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.config = JSON.parse(this.querySelector('[data-evk-config]').textContent);
      this.factors = this.config.factors;
      this.form = this.querySelector('[data-evk-form]');
      this.steps = Array.from(this.querySelectorAll('[data-evk-step]'));
      this.result = this.querySelector('[data-evk-result]');
      this.nextButton = this.querySelector('[data-evk-next]');
      this.backButton = this.querySelector('[data-evk-back]');
      this.guestsInput = this.form.elements.guests;
      this.guestsRange = this.querySelector('[data-evk-guests-range]');
      this.shareRange = this.form.elements.share;
      this.index = 0;
      this.level = 'recommended';
      this.overrides = {};
      this.receptionTouched = false;

      try {
        this.moneyFormat = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      } catch (e) {
        this.moneyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
      }
      this.numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

      this.querySelector('[data-evk-step-total]').textContent = this.steps.length;

      this.form.addEventListener('pointerdown', () => { this.viaPointer = true; });
      this.form.addEventListener('keydown', () => { this.viaPointer = false; });
      this.form.addEventListener('input', (event) => this.onFormInput(event));
      this.form.addEventListener('change', (event) => this.onFormChange(event));
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.next(); });
      this.result.addEventListener('change', (event) => this.onResultChange(event));
      this.addEventListener('click', (event) => this.onClick(event));

      this.updateGuests();
      this.setupSticky();
      this.showStep(0, false);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* ---------- Antworten ---------- */

    answers() {
      const data = new FormData(this.form);
      const guests = toInt(data.get('guests'));
      const share = clamp(toInt(data.get('share')), 5, 100);
      const mixed = share < 100;
      return {
        occasion: data.get('occasion'),
        guests,
        share,
        mixed,
        nonalc: mixed ? Math.max(1, Math.round(guests * share / 100)) : guests,
        hours: parseFloat(data.get('hours')) || 0,
        food: data.get('food'),
        line: this.result.querySelector('[data-evk-field="line"]:checked')?.value || 'senzo',
        taste: this.result.querySelector('[data-evk-field="taste"]:checked')?.value || 'mixed',
        reception: this.result.querySelector('[data-evk-field="reception"]').checked
      };
    }

    label(name) {
      const input = this.form.querySelector(`input[name="${name}"]:checked`)
        || this.result.querySelector(`[data-evk-field="${name}"]:checked`);
      return input?.dataset.label || '';
    }

    isStepValid(step) {
      if (step.dataset.evkStep === 'guests') return toInt(this.guestsInput.value) >= 1;
      return !!this.form.querySelector(`input[name="${step.dataset.evkStep}"]:checked`);
    }

    /* ---------- Schritte ---------- */

    showStep(index, focus = true) {
      clearTimeout(this.advanceTimer);
      this.index = index;
      this.steps.forEach((step, i) => { step.hidden = i !== index; });
      this.form.hidden = false;
      this.result.hidden = true;
      this.updateSticky();

      this.style.setProperty('--evk-progress', (index + 1) / this.steps.length);
      this.querySelector('[data-evk-step-current]').textContent = index + 1;
      this.backButton.hidden = index === 0;
      this.querySelector('[data-evk-next-label]').textContent = index === this.steps.length - 1 ? 'Empfehlung anzeigen' : 'Weiter';
      this.updateNav();

      if (focus) {
        this.steps[index].querySelector('legend')?.focus({ preventScroll: true });
        this.scrollToTop(this.form);
      }
    }

    updateNav() {
      this.nextButton.disabled = !this.isStepValid(this.steps[this.index]);
    }

    next() {
      clearTimeout(this.advanceTimer);
      if (!this.isStepValid(this.steps[this.index])) return;
      if (this.index < this.steps.length - 1) this.showStep(this.index + 1);
      else this.showResult();
    }

    scrollToTop(element) {
      // Stretch setzt scroll-padding-top auf Höhe des Sticky-Headers + 20 px.
      const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 100;
      const top = element.getBoundingClientRect().top;
      if (top < offset || top > window.innerHeight * 0.6) {
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: window.scrollY + top - offset, behavior: smooth ? 'smooth' : 'auto' });
      }
    }

    /* ---------- Gäste & Anteil alkoholfrei ---------- */

    setRangeFill(range) {
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      range.style.setProperty('--evk-fill', `${((parseFloat(range.value) - min) / (max - min)) * 100}%`);
    }

    updateGuests() {
      const guests = toInt(this.guestsInput.value);
      const share = toInt(this.shareRange.value);
      const nonAlc = Math.min(guests, Math.max(1, Math.round(guests * share / 100)));
      const text = share >= 100 ? `Alle ${guests} Gäste` : `ca. ${nonAlc} von ${guests} Gästen`;

      this.querySelector('[data-evk-share-value]').textContent = `${share} %`;
      this.querySelector('[data-evk-share-text]').textContent = guests >= 1 ? text : '';
      this.shareRange.setAttribute('aria-valuetext', `${share} Prozent, ${text}`);
      this.setRangeFill(this.guestsRange);
      this.setRangeFill(this.shareRange);
    }

    onFormInput(event) {
      const input = event.target;
      if (input === this.guestsRange) this.guestsInput.value = input.value;
      if (input === this.guestsInput) this.guestsRange.value = clamp(toInt(input.value), GUESTS_RANGE.min, GUESTS_RANGE.max);
      if (input === this.guestsRange || input === this.guestsInput || input === this.shareRange) this.updateGuests();
      this.updateNav();
    }

    onFormChange(event) {
      const input = event.target;
      if (input === this.guestsInput) {
        input.value = clamp(toInt(input.value), 1, toInt(input.max));
        this.guestsRange.value = clamp(toInt(input.value), GUESTS_RANGE.min, GUESTS_RANGE.max);
        this.updateGuests();
      }
      this.updateNav();

      // Kachel per Maus/Touch gewählt: automatisch weiter. Tastatur-Nutzer:innen nutzen „Weiter“.
      if (input.type === 'radio' && this.viaPointer && this.isStepValid(this.steps[this.index])) {
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => this.next(), 260);
      }
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-evk-back')) this.showStep(Math.max(0, this.index - 1));
      else if (target.hasAttribute('data-evk-restart')) this.showStep(0);
      else if (target.hasAttribute('data-evk-add')) this.addToCart();
      else if (target.hasAttribute('data-evk-row-inc') || target.hasAttribute('data-evk-row-dec')) {
        const row = target.closest('[data-evk-row]');
        const item = this.items().find((i) => i.slot.key === row.dataset.evkRow);
        if (!item) return;
        const delta = target.hasAttribute('data-evk-row-inc') ? 1 : -1;
        this.overrides[item.slot.key] = clamp(item.cartons + delta, 0, 99);
        this.renderResult();
      }
    }

    /* ---------- Ergebnis ---------- */

    resolve(line, sort, lineOnly = false) {
      const order = lineOnly ? [line] : this.config.lineFallbacks[line] || [line];
      for (const candidate of order) {
        const slot = this.config.slots.find((s) => s.line === candidate && s.sort === sort
          && (s.single.available || (s.bundle && s.bundle.available)));
        if (slot) return slot;
      }
      return null;
    }

    showResult() {
      // „Anstoßen“ je Anlass vorbelegen, solange es nicht selbst umgestellt wurde.
      if (!this.receptionTouched) {
        const occasion = this.form.querySelector('input[name="occasion"]:checked');
        this.result.querySelector('[data-evk-field="reception"]').checked = occasion?.dataset.toast === '1';
      }
      this.level = 'recommended';
      this.result.querySelector('[data-evk-level][value="recommended"]').checked = true;
      this.recalculate();

      this.form.hidden = true;
      this.result.hidden = false;
      this.result.querySelector('[data-evk-result-title]').focus({ preventScroll: true });
      this.scrollToTop(this.result);
      this.updateSticky();

      const a = this.snapshot;
      this.publish('senzo:event_configurator_result', {
        occasion: a.occasion, guests: a.guests, share: a.share, hours: a.hours, food: a.food,
        line: a.line, taste: a.taste, reception: a.reception, bottles: planBottles(this.plans.recommended)
      });
    }

    // Neu rechnen: nach den Fragen und bei jeder Änderung von Linie, Geschmack oder Anstoßen.
    recalculate() {
      const a = this.answers();
      this.snapshot = a;
      this.calc = calculate(a, this.factors);
      this.plansByLine = {};
      LINES.forEach((line) => {
        const resolve = (sort, lineOnly) => this.resolve(line, sort, lineOnly);
        this.plansByLine[line] = {};
        LEVELS.forEach((level) => { this.plansByLine[line][level] = buildPlan(this.calc, a.taste, level, this.factors, resolve); });
      });
      this.plans = this.plansByLine[a.line];
      this.overrides = {};

      LEVELS.forEach((level) => {
        this.result.querySelector(`[data-evk-level-count="${level}"]`).textContent = plural(planBottles(this.plans[level]), 'Flasche', 'Flaschen');
      });
      this.result.querySelector('[data-evk-summary]').textContent = this.summaryText(a);
      this.renderExplain(a);
      this.renderLineTotals();
      this.renderResult();
    }

    renderLineTotals() {
      LINES.forEach((line) => {
        const el = this.result.querySelector(`[data-evk-line-total="${line}"]`);
        if (el && this.plansByLine[line]) el.textContent = `${this.money(planPrice(this.plansByLine[line][this.level]))} für dein Event`;
      });
    }

    items() {
      return this.plans[this.level].map((item) => ({
        ...item,
        cartons: this.overrides[item.slot.key] ?? item.cartons
      }));
    }

    renderResult() {
      const items = this.items();
      const visible = new Set(items.map((item) => item.slot.key));
      this.result.querySelectorAll('[data-evk-row]').forEach((row) => { row.hidden = !visible.has(row.dataset.evkRow); });

      let bottles = 0;
      let price = 0;
      let saving = 0;
      items.forEach((item) => {
        const row = this.result.querySelector(`[data-evk-row="${CSS.escape(item.slot.key)}"]`);
        const pack = packaging(item.slot, item.cartons);
        bottles += pack.bottles;
        price += pack.price;
        saving += pack.saving;
        if (!row) return;

        row.querySelector('[data-evk-pack]').textContent = `6er-Karton · ${this.money(Math.round(pack.cartonPrice / CARTON))} pro Flasche`;
        row.querySelector('[data-evk-row-qty]').value = item.cartons;
        row.querySelector('[data-evk-qty-unit]').textContent = item.cartons === 1 ? 'Karton' : 'Kartons';
        row.querySelector('[data-evk-row-dec]').disabled = item.cartons <= 0;
        row.querySelector('[data-evk-price]').textContent = this.money(pack.price);
        row.querySelector('[data-evk-bottles]').textContent = plural(pack.bottles, 'Flasche', 'Flaschen');
      });

      const a = this.snapshot;
      const fallbacks = items.filter((item) => item.slot.line !== a.line);
      const fallbackEl = this.result.querySelector('[data-evk-fallback]');
      fallbackEl.hidden = !fallbacks.length;
      fallbackEl.textContent = fallbacks.length
        ? `${fallbacks.map((item) => item.slot.title).join(' und ')} ${fallbacks.length > 1 ? 'ergänzen' : 'ergänzt'} deine Auswahl, weil es bei ${this.label('line')} ${fallbacks.map((item) => MISSING_SORT[item.sort]).join(' und ')} gibt.`
        : '';

      this.result.querySelector('[data-evk-total-bottles]').textContent = bottles;
      this.result.querySelector('[data-evk-total-cartons]').textContent = plural(bottles / CARTON, 'Karton', 'Kartons');
      this.result.querySelector('[data-evk-total-price]').textContent = this.money(price);
      this.querySelector('[data-evk-sticky-bottles]').textContent = plural(bottles, 'Flasche', 'Flaschen');
      this.querySelector('[data-evk-sticky-price]').textContent = this.money(price);

      this.result.querySelector('[data-evk-average]').textContent = bottles > 0
        ? `Im Schnitt ${this.money(Math.round(price / bottles))} pro Flasche`
        : '';

      // Pro-Gast-Preis nur bei komplett alkoholfreien Events. Bei gemischten Events wäre er wegen der Umsteiger schief.
      this.result.querySelector('[data-evk-per-guest]').textContent = !a.mixed && a.guests > 0
        ? `ca. ${this.money(Math.round(price / a.guests))} pro Gast`
        : '';

      const savingEl = this.result.querySelector('[data-evk-saving]');
      savingEl.hidden = saving <= 0;
      savingEl.textContent = `Du sparst ${this.money(saving)} mit Karton-Preisen`;

      const shippingEl = this.result.querySelector('[data-evk-shipping]');
      const threshold = this.config.freeShippingCents;
      shippingEl.hidden = this.config.country !== 'DE' || !threshold || price <= 0;
      shippingEl.classList.toggle('is-free', price >= threshold);
      shippingEl.textContent = price >= threshold
        ? 'Versandkostenfrei innerhalb Deutschlands'
        : `Noch ${this.money(threshold - price)} bis zum kostenlosen Versand`;

      const b2b = this.result.querySelector('[data-evk-b2b]');
      if (b2b) b2b.hidden = !(bottles >= this.config.b2bThreshold || a.occasion === 'firmenevent');

      this.querySelectorAll('[data-evk-add]').forEach((button) => {
        if (button.getAttribute('aria-busy') !== 'true') button.disabled = bottles <= 0;
      });
    }

    onResultChange(event) {
      const input = event.target;
      if (input.hasAttribute('data-evk-level')) {
        this.level = input.value;
        this.overrides = {};
        this.renderLineTotals();
        this.renderResult();
      } else if (input.hasAttribute('data-evk-row-qty')) {
        const key = input.closest('[data-evk-row]').dataset.evkRow;
        this.overrides[key] = clamp(toInt(input.value), 0, 99);
        this.renderResult();
      } else if (input.dataset.evkField) {
        if (input.dataset.evkField === 'reception') this.receptionTouched = true;
        this.recalculate();
      }
    }

    summaryText(a) {
      const guests = a.mixed ? `${a.guests} Gäste, davon ${a.share} % alkoholfrei` : `${a.guests} Gäste, alle alkoholfrei`;
      return [this.label('occasion'), guests, this.label('hours'), this.label('food')].join(' · ');
    }

    renderExplain(a) {
      const f = this.factors;
      const c = this.calc;
      const n = (x) => this.numberFormat.format(x);
      const lines = [];

      if (a.mixed) {
        lines.push(`<strong>${c.nonAlc} von ${a.guests} Gästen</strong> trinken alkoholfrei (${a.share} %).`);
        if (c.switchers > 0) lines.push(`Dazu kommen <strong>${n(c.switchers)} Gäste</strong>, die zwischendurch umsteigen (${f.switcherShare} % der übrigen).`);
      } else {
        lines.push(`<strong>Alle ${a.guests} Gäste</strong> trinken alkoholfrei.`);
      }
      if (a.reception) lines.push(`Anstoßen: <strong>${n(c.sparklingGlasses)} Gläser</strong> Prickelndes pro Gast.`);
      if (c.mealGlasses > 0) lines.push(`${escapeHtml(this.label('food'))}: <strong>${n(c.mealGlasses)} Gläser</strong> Wein pro Gast.`);
      if (c.freeHours > 0) lines.push(`Übrige ${n(c.freeHours)} Stunden: <strong>${n(c.freeGlasses)} Gläser</strong> pro Gast.`);
      if (a.taste === 'sparkling') lines.push('Bei „Prickelnd“ planen wir die Hälfte als Schaumwein.');
      if (c.occasionFactor !== 1) {
        const percent = Math.round((c.occasionFactor - 1) * 100);
        lines.push(`${escapeHtml(this.label('occasion'))}: <strong>${Math.abs(percent)} % ${percent > 0 ? 'mehr' : 'weniger'}</strong> als bei einer normalen Feier.`);
      }
      lines.push(`Eine Flasche reicht für <strong>${n(BOTTLE_ML / f.glassMlWine)} Gläser Wein</strong> oder <strong>${n(BOTTLE_ML / f.glassMlSparkling)} Gläser Prickelndes</strong>.`);
      lines.push(`Wir rechnen ${f.bufferRecommended} % Puffer dazu und runden auf volle 6er-Kartons auf. „Mit Reserve“ ist mindestens ein Karton mehr.`);

      this.result.querySelector('[data-evk-explain]').innerHTML = lines.map((line) => `<li>${line}</li>`).join('');
    }

    /* ---------- Mobile Warenkorb-Leiste ---------- */

    setupSticky() {
      this.sticky = this.querySelector('[data-evk-sticky]');
      const summary = this.querySelector('[data-evk-summary-box]');
      if (!this.sticky || !summary || !('IntersectionObserver' in window)) return;
      this.visibility = { result: false, summary: false };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { this.visibility[entry.target === summary ? 'summary' : 'result'] = entry.isIntersecting; });
        this.updateSticky();
      });
      this.observer.observe(this.result);
      this.observer.observe(summary);
    }

    updateSticky() {
      if (!this.sticky) return;
      const show = !this.result.hidden && this.visibility?.result && !this.visibility.summary;
      this.sticky.classList.toggle('is-visible', !!show);
    }

    money(cents) {
      return this.moneyFormat.format(cents / 100);
    }

    publish(name, payload) {
      try { window.Shopify?.analytics?.publish?.(name, payload); } catch (e) { /* Analytics darf nie den Kauf blockieren */ }
    }

    /* ---------- Warenkorb ---------- */

    cartItems() {
      const quantities = new Map();
      const add = (id, qty) => { if (qty > 0) quantities.set(id, (quantities.get(id) || 0) + qty); };
      this.items().forEach((item) => {
        const pack = packaging(item.slot, item.cartons);
        if (pack.bundle) add(pack.bundle.id, item.cartons);
        else add(item.slot.single.id, pack.bottles);
      });
      return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
    }

    attributeText() {
      const a = this.snapshot;
      const levelLabel = { recommended: 'Empfohlen', reserve: 'Mit Reserve' }[this.level];
      return [
        this.summaryText(a),
        this.label('line'),
        this.label('taste'),
        a.reception ? 'mit Anstoßen' : 'ohne Anstoßen',
        `${levelLabel}: ${planBottles(this.items())} Fl. (Vorschlag ${planBottles(this.plans[this.level])})`
      ].join(' · ');
    }

    setBusy(busy) {
      this.querySelectorAll('[data-evk-add]').forEach((button) => {
        const label = button.querySelector('[data-evk-add-label]');
        label.dataset.text ??= label.textContent;
        label.textContent = busy ? 'Wird hinzugefügt …' : label.dataset.text;
        button.disabled = busy;
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      });
    }

    async addToCart() {
      const errorEl = this.result.querySelector('[data-evk-error]');
      const items = this.cartItems();
      if (!items.length || this.querySelector('[data-evk-add][aria-busy="true"]')) return;

      const root = window.Shopify?.routes?.root || '/';
      const html = document.documentElement;
      const sections = new Set();
      html.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      this.setBusy(true);
      errorEl.hidden = true;
      html.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: [...sections].join(',') })
        });
        const json = await response.json();
        if (!response.ok) throw new Error(json.description || json.message || 'Das hat leider nicht geklappt.');

        // Eingaben als Warenkorb-Attribut, stehen in der Bestellung unter „Zusätzliche Details“.
        const attributeRequest = fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Event-Konfigurator': this.attributeText() } })
        }).catch(() => {});

        this.publish('senzo:event_configurator_add_to_cart', { items: items.length, bottles: planBottles(this.items()) });

        const settings = window.themeVariables?.settings || {};
        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          await attributeRequest;
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json.sections;
        html.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } catch (error) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message, tone: 'error' } }));
        html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } finally {
        html.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.setBusy(false);
      }
    }
  }

  window.customElements.define('event-configurator', EventConfigurator);
})();
